import * as React from 'react';
import Cookies from 'js-cookie';
import RouterSingleton from 'next/router';

import { useTrackingContext } from '@zapier/toolbox-tracking';
import { useCurrentAccountId } from '@zapier/identity';
import { isDatadog } from '../../../../identity-utils/src/isDatadog';
import { captureException } from '@sentry/nextjs';

const promiseNoop = () => Promise.resolve();

type Events = {
  ['navigate']: Record<string, unknown>;
  ['schema:web_analytics.tracking.page_view.PageViewEvent']: Record<
    string,
    unknown
  >;
};

export type EventName = keyof Events;
export type EventData<K extends EventName> = Events[K];

export type TrackEvent = <K extends EventName>(
  eventName: K,
  data: EventData<K>
) => Promise<void>;

/**
 * Wrap useTrackingContext and expose only the strongly typed trackEvent
 * All trackEvent usages should flow through this rather than using useTrackingContext() directly
 */
export function useTrackEvent(): TrackEvent {
  const { trackEvent } = useTrackingContext();
  const currentAccountId = useCurrentAccountId();

  const track: TrackEvent = React.useCallback(
    async function (eventName: string, data: any) {
      try {
        await trackEvent(eventName, {
          account_id: currentAccountId,
          identity_id: Cookies.get('zapidentity'),
          path: window.location.pathname,
          pageRoute: RouterSingleton.route,
          ...data,
        });
      } catch (err) {
        captureException(err, {
          tags: {
            section: RouterSingleton.route,
            type: 'TrackEvent',
          },
        });
      }
      return;
    },
    [currentAccountId, trackEvent]
  );
  // Don't track events when we're serving this from a Datadog synthetic test, otherwise it could skew our metrics / KPIs!
  if (isDatadog()) {
    return promiseNoop;
  }
  return track;
}
